import Vue from 'vue'
import Router from 'vue-router'
import Home from './views/Home.vue'
import vegdetail from './views/vegdetail.vue'
// import App from './App.vue'

import whyeatseasonally from './views/whyeatseasonally.vue'
import about from './views/about.vue'
import gettheapp from './views/gettheapp.vue'
import newsletter from './views/newsletter.vue'
import press from './views/press.vue'
import mediakit from './views/mediakit.vue'
import NotFound from './views/NotFound.vue'
import {
    isValidState,
    isValidMonth,
    isValidCard
} from './components/api/routeValidation.js'

Vue.use(Router)

// ---------------------------------------------------------------------------
// State URL aliases
// Generic "california" / "florida" URLs are rewritten to the regional defaults
// used by the app (Southern California / South Florida).
// ---------------------------------------------------------------------------
const STATE_ALIASES = {
    'california': 'southern-california',
    'florida': 'south-florida'
}

function normalizeState (slug) {
    return STATE_ALIASES[slug] || slug
}

// ---------------------------------------------------------------------------
// URL param validation wrapper
//
// Wraps a route component so invalid :state / :month / :card slugs render
// NotFound IN PLACE (same URL kept in the address bar).
//
// Important: do NOT redirect to { name: 'not-found' } for path '*'.
// That resolves to "/" and incorrectly lands on the homepage.
// ---------------------------------------------------------------------------
function withValidation (component, validate) {
    return {
        name: 'ValidatedRoute',
        props: ['state', 'month', 'card'],
        render (h) {
            if (!validate(this.$route)) {
                return h(NotFound)
            }
            return h(component, {
                props: {
                    state: this.state,
                    month: this.month,
                    card: this.card
                }
            })
        }
    }
}

// Produce listing routes (Home grid). Used for scroll save/restore when
// opening and closing a produce detail card.
export const LISTING_ROUTES = ['home', 'selectstate', 'homestate']

// Scroll Y on the listing page, captured when navigating to vegdetail.
// Restored in App.vue after the page-fade enter (not in scrollBehavior),
// because with mode="out-in" the page is too short during the gap and the
// browser clamps scroll to the top.
let homeScrollY = null

/** Read + clear the saved listing scroll Y (used by App.vue @after-enter). */
export function consumeListingScrollY () {
    const y = homeScrollY
    homeScrollY = null
    return y
}

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/',
            name: 'home',
            component: Home,
            props:true
        },
        // /state/:state — state only (e.g. /state/alabama)
        // Invalid state slug → NotFound (URL unchanged)
        {
            path: '/state/:state',
            name: 'selectstate',
            component: withValidation(Home, (route) => isValidState(route.params.state)),
            props:true
        },
        // /:state/:month — state + month (e.g. /alabama/early-january)
        // Invalid state and/or month → NotFound (URL unchanged)
        {
            path: '/:state/:month',
            name: 'homestate',
            component: withValidation(
                Home,
                (route) => isValidState(route.params.state) && isValidMonth(route.params.month)
            ),
            props:true
        },
        // /veg/:card/:state — produce detail (e.g. /veg/mangoes/alabama)
        // Invalid card and/or state → NotFound (URL unchanged)
        {
            path: '/veg/:card/:state',
            name: 'vegdetail',
            component: withValidation(
                vegdetail,
                (route) => isValidCard(route.params.card) && isValidState(route.params.state)
            ),
            props:true
        },

        {
            path: '/why-eat-seasonally',
            name: 'whyeatseasonally',
            component: whyeatseasonally
        },
        {
            path: '/about',
            name: 'about',
            component: about
        },
        {
            path: '/download-app',
            name: 'gettheapp',
            component: gettheapp
        },
        {
            path: '/newsletter',
            name: 'newsletter',
            component: newsletter
        },
        {
            path: '/press',
            name: 'press',
            component: press
        },
        {
            path: '/media-kit',
            name: 'mediakit',
            component: mediakit
        },
        {
            path: '*',
            name: 'not-found',
            component: NotFound
        }
    ],
    scrollBehavior (to, from, savedPosition) {
        // Browser back/forward: use the position Vue Router saved
        if (savedPosition) {
            return savedPosition
        }

        // Closing produce detail (card "x"): do NOT scroll here.
        // Restore happens in App.vue @after-enter once the listing has height
        // (scrolling too early lets the browser clamp Y to the top).
        if (
            from.name === 'vegdetail' &&
            LISTING_ROUTES.includes(to.name) &&
            homeScrollY !== null
        ) {
            return false
        }

        // Opening produce detail (card click or produce filter) → same offset
        // as static pages in App.vue ($scrollToTop(330) / $scrollToTop(200)),
        // so the full hero header is scrolled past and content is in view.
        if (to.name === 'vegdetail') {
            const y = window.innerWidth <= 940 ? 200 : 330
            return { x: 0, y }
        }

        // Default: scroll to top on every other navigation
        return { x: 0, y: 0 }
      },
})

// ---------------------------------------------------------------------------
// Global navigation guards: placeholder redirects + state aliases + scroll save
// ---------------------------------------------------------------------------
router.beforeEach((to, from, next) => {
    // Save listing scroll position before opening a produce card, so closing
    // the card can restore it (see scrollBehavior above).
    if (to.name === 'vegdetail' && LISTING_ROUTES.includes(from.name)) {
        homeScrollY = window.scrollY || window.pageYOffset || 0
    }

    // /state/select-a-state → homepage
    if (to.name === 'selectstate' && to.params.state === 'select-a-state') {
        return next({ path: '/', replace: true })
    }

    // /:state/any-month → /state/:state
    if (to.name === 'homestate' && to.params.month === 'any-month') {
        return next({
            name: 'selectstate',
            params: { state: normalizeState(to.params.state) },
            replace: true
        })
    }

    // /veg/any-produce/:state → /state/:state
    if (to.name === 'vegdetail' && to.params.card === 'any-produce') {
        return next({
            name: 'selectstate',
            params: { state: normalizeState(to.params.state) },
            replace: true
        })
    }

    // california → southern-california, florida → south-florida
    // (applies on any route that has a :state param)
    if (to.params.state && STATE_ALIASES[to.params.state]) {
        return next({
            name: to.name,
            params: { ...to.params, state: STATE_ALIASES[to.params.state] },
            query: to.query,
            hash: to.hash,
            replace: true
        })
    }

    next()
})

export default router
